import _ from 'lodash'
import { createRef } from '@wix/materializer'
import refData from '@wix/document-services-json-schemas/dist/refData.json'
import queryMappingMap from '@wix/document-services-json-schemas/dist/queryMapping.json'
import { DataMapName, QueryProperty, Language, pointers } from '@wix/thunderbolt-becky-types'
import type { Schemas } from '../types'

const {
	data: {
		DATA_MAPS: { LAYOUT, STYLE, VARIABLES },
	},
} = pointers

/** namespaces */
export const SITE_NAMESPACE = 'site' as const
export const RESOLVED_REFS_NAMESPACE = 'resolvedByRef' as const
export const ENVIRONMENT_NAMESPACE = 'environment' as const
export const INVALIDATIONS_NAMESPACE = 'widget_invalidations'

/** aspects */
// map refComp id to template data (key: appDefinitionId_widgetId, rootCompId: string, sharedPart: boolean)
export const REF_COMPONENTS_TEMPLATE_DATA_ASPECT = 'refComponentsTemplateData' as const
// map inflatedRefComp id to it's own presets and ancestors resolved presets
export const RESOLVED_REF_COMP_PRESETS_ASPECT = 'resolvedRefCompPresets' as const
export const STRUCTURE_ASPECT = 'structure' as const
export const RESOLVED_REF_IS_GHOST_COMPONENT_ASPECT = 'isGhostComponent' as const
// map data query to comp id (needed for connectionList dataNode)
export const DATA_QUERY_TO_COMP_ID_ASPECT = 'originalDataQueryToCompId' as const
// map original comp id to inflated comp id (used by ObsoleteBehaviorsList dataNode). Relevant only to internal refs
export const COMP_ID_TO_INFLATED_COMP_ID_ASPECT = 'originalCompIdToInflatedCompId' as const
export const INFLATED_COMP_ID_TO_TEMPLATE_COMP = 'inflatedCompIdToTemplateComp' as const
// stores translations per namespace (site / widget template)
export const RAW_TRANSLATIONS_ASPECT = 'rawTranslations' as const
// stores template root comp id
export const ROOT_COMP_ID_ASPECT = 'rootCompId' as const
// stores resolved translations based on the language and original language env vars
export const RESOLVED_TRANSLATIONS_ASPECT = 'resolvedTranslations' as const
// stores all system styles defined in masterPage theme_data map
export const SYSTEM_STYLES = 'systemStyle' as const
// sotres inflated refArrays
export const INFLATED_MAP = 'InflatedMap' as const
export const VARIABLES_INFLATED_MAP = `variablesQuery${INFLATED_MAP}`
export const LAYOUT_INFLATED_MAP = `layoutQuery${INFLATED_MAP}`
export const STYLE_INFLATED_MAP = `styleId${INFLATED_MAP}`

/** refs */
export const KEEP_GHOST_COMPONENTS_REF = createRef<boolean>([
	ENVIRONMENT_NAMESPACE,
	ENVIRONMENT_NAMESPACE,
	'keepGhostComponents',
])
export const LANGUAGE_REF = createRef<Language>([ENVIRONMENT_NAMESPACE, ENVIRONMENT_NAMESPACE, 'language'])
export const ORIGINAL_LANGUAGE_REF = createRef<Language>([
	ENVIRONMENT_NAMESPACE,
	ENVIRONMENT_NAMESPACE,
	'originalLanguage',
])

/** consts */
export const QUERY_MAPPING = queryMappingMap as Record<QueryProperty, DataMapName>
export const DATA_MAP_TO_QUERY = _.invert(queryMappingMap) as Record<DataMapName, QueryProperty>
export const DATA_MAP_NAMES = new Set(Object.values(queryMappingMap) as Array<DataMapName>)
export const EMPTY_REMOVED: Array<Array<string | number>> = []
export const EMPTY_SCHEMA = { schema: undefined, removed: EMPTY_REMOVED }
export const SCHEMAS: NonNullable<Schemas> = _.mapKeys(refData, (__, key) => key.replace('.json', ''))

export const OVERRIDES_MAP_DELIMITER = '_OM_'
export const LEAFS_OVERRIDES_DELIMITER = '$$$$'
export const GHOST_REF_COMP_STRUCTURE_OVERRIDES = {
	components: [],
	componentType: 'GhostRefComp',
	connectionQuery: null,
	uiType: '',
}
export const PRESET_RESOLVING_DATA_ITEM_TYPE = {
	[LAYOUT]: 'layout',
	[STYLE]: 'style',
	[VARIABLES]: 'variable',
}

export const INFLATED_MAP_TO_DATA_MAP_NAME: Record<string, DataMapName> = {
	[LAYOUT_INFLATED_MAP]: LAYOUT,
	[STYLE_INFLATED_MAP]: STYLE,
	[VARIABLES_INFLATED_MAP]: VARIABLES,
}
